import { createFileRoute } from '@tanstack/react-router';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  NumberInput,
  Skeleton,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';

interface DraftLanguage {
  id: number | null;
  code: string;
  name: string;
  nativeName: string;
  rtl: boolean;
  enabled: boolean;
  sortOrder: number;
}

const EMPTY: DraftLanguage = {
  id: null,
  code: '',
  name: '',
  nativeName: '',
  rtl: false,
  enabled: true,
  sortOrder: 0,
};

function LanguagesPage() {
  const list = trpc.admin.languages.list.useQuery();
  const utils = trpc.useUtils();

  const create = trpc.admin.languages.create.useMutation({
    onSuccess: () => {
      void utils.admin.languages.list.invalidate();
      notifications.show({ message: 'Language added', color: 'green' });
      setOpened(false);
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const update = trpc.admin.languages.update.useMutation({
    onSuccess: () => {
      void utils.admin.languages.list.invalidate();
      notifications.show({ message: 'Language updated', color: 'green' });
      setOpened(false);
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const del = trpc.admin.languages.delete.useMutation({
    onSuccess: () => {
      void utils.admin.languages.list.invalidate();
      notifications.show({ message: 'Language deleted', color: 'orange' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const [opened, setOpened] = useState(false);
  const [draft, setDraft] = useState<DraftLanguage>(EMPTY);

  const openCreate = () => {
    setDraft(EMPTY);
    setOpened(true);
  };

  const openEdit = (row: NonNullable<typeof list.data>[number]) => {
    setDraft({
      id: row.id,
      code: row.code,
      name: row.name,
      nativeName: row.nativeName ?? '',
      rtl: row.rtl,
      enabled: row.enabled,
      sortOrder: row.sortOrder,
    });
    setOpened(true);
  };

  const save = () => {
    const base = {
      name: draft.name,
      nativeName: draft.nativeName || null,
      rtl: draft.rtl,
      enabled: draft.enabled,
      sortOrder: draft.sortOrder,
    };
    if (draft.id == null) {
      create.mutate({ code: draft.code, ...base });
    } else {
      update.mutate({ id: draft.id, ...base });
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Languages</Title>
          <Button leftSection={<IconPlus size={14} />} onClick={openCreate}>
            Add language
          </Button>
        </Group>

        <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
          <Text size="sm">
            The locale registry drives the public language switcher and the Translations page.
            Bundled defaults exist for <code>en</code> and <code>hi</code>; adding a new language
            here lets SuperAdmin start writing override entries for it under{' '}
            <b>System → Translations</b>. Disabling a row removes it from the vendor switcher
            without losing its overrides.
          </Text>
        </Alert>

        <Card p={0}>
          {list.isPending ? (
            <Skeleton height={240} m="md" />
          ) : (
            <Table withRowBorders={false} striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Code</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Native</Table.Th>
                  <Table.Th>RTL</Table.Th>
                  <Table.Th>Enabled</Table.Th>
                  <Table.Th>Order</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {list.data!.map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td>
                      <Text ff="monospace" size="sm">
                        {row.code}
                      </Text>
                    </Table.Td>
                    <Table.Td>{row.name}</Table.Td>
                    <Table.Td>
                      <Text c={row.nativeName ? undefined : 'dimmed'}>{row.nativeName ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      {row.rtl ? (
                        <Badge color="violet" variant="light" size="sm">
                          rtl
                        </Badge>
                      ) : (
                        <Text size="xs" c="dimmed">
                          ltr
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {row.enabled ? (
                        <Badge color="green" variant="light" size="sm">
                          enabled
                        </Badge>
                      ) : (
                        <Badge color="gray" variant="light" size="sm">
                          disabled
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>{row.sortOrder}</Table.Td>
                    <Table.Td align="right">
                      <Group gap="xs" justify="flex-end">
                        <ActionIcon
                          variant="subtle"
                          onClick={() => openEdit(row)}
                          aria-label="Edit"
                        >
                          <IconEdit size={14} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => {
                            if (
                              confirm(
                                `Delete ${row.name} (${row.code})? Existing overrides for this locale will remain in the Configuration table but become unreachable.`,
                              )
                            ) {
                              del.mutate({ id: row.id });
                            }
                          }}
                          aria-label="Delete"
                          disabled={row.code === 'en'}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {list.data!.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Text c="dimmed" ta="center" py="md">
                        No languages registered yet.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      </Stack>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={draft.id == null ? 'Add language' : `Edit ${draft.name}`}
        centered
      >
        <Stack gap="sm">
          <TextInput
            label="BCP-47 code"
            description="e.g. en, hi, ar, pt-BR. Cannot be changed after creation."
            placeholder="ar"
            value={draft.code}
            onChange={(e) => setDraft({ ...draft, code: e.currentTarget.value })}
            disabled={draft.id != null}
          />
          <TextInput
            label="English name"
            placeholder="Arabic"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.currentTarget.value })}
          />
          <TextInput
            label="Native name (optional)"
            description="Shown in the language switcher dropdown."
            placeholder="العربية"
            value={draft.nativeName}
            onChange={(e) => setDraft({ ...draft, nativeName: e.currentTarget.value })}
          />
          <Switch
            label="Right-to-left script"
            description="Flips the document dir attribute for Arabic, Hebrew, Persian, etc."
            checked={draft.rtl}
            onChange={(e) => setDraft({ ...draft, rtl: e.currentTarget.checked })}
          />
          <Switch
            label="Enabled"
            description="Disabling hides the locale from the vendor language switcher."
            checked={draft.enabled}
            onChange={(e) => setDraft({ ...draft, enabled: e.currentTarget.checked })}
          />
          <NumberInput
            label="Sort order"
            description="Lower numbers appear first in the language switcher."
            min={0}
            max={9999}
            value={draft.sortOrder}
            onChange={(v) => setDraft({ ...draft, sortOrder: Number(v) || 0 })}
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => setOpened(false)}>
              Cancel
            </Button>
            <Button
              leftSection={<IconCheck size={14} />}
              loading={create.isPending || update.isPending}
              onClick={save}
              disabled={!draft.code.trim() || !draft.name.trim()}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </motion.div>
  );
}

export const Route = createFileRoute('/central/languages')({ component: LanguagesPage });
